// components/industry-templates/UniversalEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Info, MapPin, Calendar, Users, Tag } from 'lucide-react';

export const UniversalEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data || {};
  const industryName = structure.industry?.replace('-', ' ') || 'Event';

  return (
    <div className="space-y-8">
      {/* Event Description */}
      <Card className="border-0 bg-gradient-to-br" style={{ background: `linear-gradient(to bottom right, ${theme.primary}20, ${theme.secondary}20)` }}>
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-2xl">
            <Info className="w-8 h-8" style={{ color: theme.primary }} />
            About This {industryName.charAt(0).toUpperCase() + industryName.slice(1)} Event
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-300 text-lg leading-relaxed">{structure.description}</p>
        </CardContent>
      </Card>

      {/* Topics/Tags */}
      {structure.topics && structure.topics.length > 0 && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Tag className="w-6 h-6" style={{ color: theme.primary }} />
              Topics & Focus Areas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {structure.topics.map((topic: string, idx: number) => (
                <Badge key={idx} style={{ backgroundColor: theme.primary }} className="text-sm py-2 px-4">
                  {topic}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Event Details Grid */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="w-6 h-6" style={{ color: theme.primary }} />
            Event Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Event Type</p>
              <p className="text-white font-semibold capitalize">{structure.event_type}</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Format</p>
              <p className="text-white font-semibold capitalize">{structure.format}</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Capacity</p>
              <p className="text-white font-semibold">{structure.capacity} attendees</p>
            </div>
            {structure.location?.city && (
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">Location</p>
                <p className="text-white font-semibold">{structure.location.city}, {structure.location.state}</p>
              </div>
            )}
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Duration</p>
              <p className="text-white font-semibold">
                {new Date(structure.start_date).toLocaleDateString()} - {new Date(structure.end_date).toLocaleDateString()}
              </p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Timezone</p>
              <p className="text-white font-semibold">{structure.timezone}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Industry-Specific Data (if any) */}
      {industryData && Object.keys(industryData).length > 0 && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Industry Highlights</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(industryData).map(([key, value]: [string, any], idx) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <p className="text-xs text-slate-400 capitalize mb-2">{key.replace(/_/g, ' ')}</p>
                  <p className="text-white font-semibold">
                    {typeof value === 'boolean' ? (value ? '✓ Available' : '✗ Not Available') : 
                     typeof value === 'object' ? '📊 Details Available' :
                     String(value)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organizer Info */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-6 h-6" style={{ color: theme.primary }} />
            Organized By
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="text-white font-bold text-lg">{structure.organizer_name}</p>
            {structure.organizer_email && (
              <p className="text-slate-400">📧 {structure.organizer_email}</p>
            )}
            {structure.organizer_phone && (
              <p className="text-slate-400">📞 {structure.organizer_phone}</p>
            )}
            {structure.organizer_website && (
              <a href={structure.organizer_website} target="_blank" rel="noopener noreferrer" 
                 className="text-blue-400 hover:underline">
                🌐 {structure.organizer_website}
              </a>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
